import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

export const generateFinalSignature = async (payload, code) => {
  const message = JSON.stringify(payload);

  const hmac = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(code),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign(
    'HMAC',
    hmac,
    new TextEncoder().encode(message)
  );

  const finalSignature = btoa(
    String.fromCharCode(...new Uint8Array(signature))
  );

  return finalSignature;
};

export const fetchProgressionByCourseIdAction = createAsyncThunk(
  'progression/fetchProgressionByCourseIdAction',
  async (courseId) => {
    try {
      const response = await axios.get(`/courses/${courseId}/progression`);
      return response.data;
    } catch (error) {
      return error.message;
    }
  }
);

export const postProgressionAction = createAsyncThunk(
  'progression/postProgressionAction',
  async ({ classId, courseId, questId, subQuestId, code }) => {
    try {
      const payload = {
        CourseId: courseId,
        QuestId: questId,
        SubQuestId: subQuestId,
      };

      const finalSignature = await generateFinalSignature(payload, code);

      if (classId) payload.ClassId = classId;

      const response = await axios.put('/progressions', payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-signature': finalSignature,
        },
      });

      return response.data;
    } catch (error) {
      return error.message;
    }
  }
);

export const postInteractiveSessionAction = createAsyncThunk(
  'progression/postInteractiveSessionAction',
  async ({
    classId,
    courseId,
    questId,
    subQuestId,
    code,
    isSuccess,
    duration,
    score,
    maxScore,
    passingScore,
  }) => {
    try {
      const payload = {
        CourseId: courseId,
        QuestId: questId,
        SubQuestId: subQuestId,
      };

      const finalSignature = await generateFinalSignature(payload, code);

      if (classId) payload.ClassId = classId;

      const response = await axios.put(
        '/progressions/interactive-content-session',
        {
          ...payload,
          isSuccess,
          duration,
          score,
          passingScore,
          maxScore,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-signature': finalSignature,
          },
        }
      );

      return response.data;
    } catch (error) {
      return error.message;
    }
  }
);

export const postFileSubmissionSessionAction = createAsyncThunk(
  'progression/postFileSubmissionSessionAction',
  async (
    {
      classId,
      courseId,
      questId,
      subQuestId,
      code,
      submittedFiles,
      filesToRemove,
    },
    { rejectWithValue }
  ) => {
    try {
      const payload = {
        CourseId: courseId,
        QuestId: questId,
        SubQuestId: subQuestId,
      };

      const finalSignature = await generateFinalSignature(payload, code);

      if (classId) payload.ClassId = classId;

      const formData = new FormData();
      formData.append('CourseId', courseId);
      formData.append('QuestId', questId);
      formData.append('SubQuestId', subQuestId);

      if (submittedFiles && submittedFiles.length > 0) {
        submittedFiles.forEach((file) => {
          formData.append('SubmittedFiles', file);
        });
      }

      if (filesToRemove && filesToRemove.length > 0) {
        filesToRemove.forEach((file) => {
          formData.append('FilesToRemove', file);
        });
      }

      if (classId) {
        formData.append('ClassId', classId);
      }

      const response = await axios.put(
        '/file-submission-session/record',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-signature': finalSignature,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error.response.status,
        data: error.response.data,
      });
    }
  }
);

export const fetchSubQuestsByQuestIdAction = createAsyncThunk(
  'progression/fetchSubQuestsByQuestIdAction',
  async ({ classId, courseId, questId }) => {
    try {
      const response = await axios.get(
        `${classId !== 0 ? `/classes/${classId}/courses` : '/courses'}/${courseId}/quests/${questId}/sub-quests`
      );
      return {
        questId,
        subQuests: response.data,
      };
    } catch (error) {
      return error.message;
    }
  }
);
